import type { ReactNode } from "react";
import { token } from "@/design/theme";

export interface Step {
  id: string;
  label: ReactNode;
}

/**
 * Stepper — the wizard progress rail. Each step is color + number/glyph + text (never color alone).
 * A completed step is clickable (to jump back); a future step is disabled. Renders as an ordered
 * list for AT, with aria-current on the active step. Mirrors correctly under RTL (logical layout).
 */
export function Stepper({
  steps,
  activeIndex,
  maxReached,
  onJump,
}: {
  steps: ReadonlyArray<Step>;
  activeIndex: number;
  /** Highest step index the user has reached (steps beyond are locked). */
  maxReached: number;
  onJump: (index: number) => void;
}) {
  return (
    <ol
      style={{
        display: "flex",
        listStyle: "none",
        margin: 0,
        padding: 0,
        gap: token.s2,
        flexWrap: "wrap",
        marginBlockEnd: token.s5,
      }}
      aria-label="Étapes"
    >
      {steps.map((step, i) => {
        const done = i < activeIndex;
        const active = i === activeIndex;
        const reachable = i <= maxReached;
        const tone = active ? token.azure : done ? token.success : token.steel;
        const bg = active ? token.azureSoft : done ? token.tintSuccess : token.surface2;
        return (
          <li key={step.id} style={{ flex: "1 1 160px", minWidth: 0 }}>
            <button
              type="button"
              onClick={() => reachable && onJump(i)}
              disabled={!reachable}
              aria-current={active ? "step" : undefined}
              style={{
                display: "flex",
                alignItems: "center",
                gap: token.s2,
                inlineSize: "100%",
                padding: `${token.s2} ${token.s3}`,
                border: `1px solid ${active ? token.azure : token.line}`,
                borderRadius: token.rMd,
                background: bg,
                color: token.ink,
                font: "inherit",
                textAlign: "start",
                cursor: reachable ? "pointer" : "default",
                minBlockSize: token.minTouch,
              }}
            >
              <span
                aria-hidden
                className="nv-mono nv-ltr-island"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  inlineSize: 22,
                  blockSize: 22,
                  flex: "0 0 auto",
                  borderRadius: token.rPill,
                  background: tone,
                  color: token.white,
                  fontSize: token.fsCaption,
                  fontWeight: 700,
                }}
              >
                {done ? "✓" : i + 1}
              </span>
              <span style={{ fontSize: token.fsLabel, fontWeight: active ? 600 : 500, color: tone }}>
                {step.label}
              </span>
            </button>
          </li>
        );
      })}
    </ol>
  );
}
